import React from 'react';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import Name from 'components/Form/Name';
import PVQName from 'components/Form/PVQName/Name';
import DateControl from 'components/Form/DateControl';
import Country from 'components/Form/Country';
import Telephone from 'components/Form/Telephone';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import Show from 'components/Form/Show';
import NotApplicable from 'components/Form/NotApplicable';
import Location from 'components/Form/Location';
import {
  CommonOnError, CommonOnUpdate, CommonRequired, CommonScrollIntoView
} from 'types/props';
import ValidationElement, { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { RelationshipsDivorcedItemModel } from 'forms/sections/relationships/marital/types';
import {
  CountryArrayModel, CountryModel, DateModel, LocationModel
} from 'forms/shared/types';
import Comments from 'components/Form/Comments';
import { EmptyUSLocationInstance } from 'forms/shared/validators/models/location/instance';
import { isPVQForm } from 'helpers/form';
import EmailCollection from 'components/Form/PVQEmail/EmailCollection';
import LocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import { isSafeInternational } from 'helpers/locations';
import { pickDate } from '../../../../validators/helpers';

interface Props extends Partial<RelationshipsDivorcedItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  requireRelationshipMaritalForeignBornDocExpiration?: boolean
  requireRelationshipMaritalDivorcePhoneNumber?: boolean
  scrollIntoView?: CommonScrollIntoView
  required?: CommonRequired
  applicantBirthdate?: DateModel
  attribute?: string
  formType?: string
  maritalStatus: string
}

export default class Divorce extends ValidationElement<Props> {
  handleNameError = (value, arr) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code:
        err.code.indexOf('required') !== -1
          ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
          : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<RelationshipsDivorcedItemModel>) => {
    this.props.onUpdate({
      Name: this.props.Name,
      Birthdate: this.props.Birthdate,
      BirthPlace: this.props.BirthPlace,
      Citizenship: this.props.Citizenship,
      Telephone: this.props.Telephone,
      Emails: this.props.Emails, // pvq only
      Recognized: this.props.Recognized,
      Address: this.props.Address,
      DateDivorced: this.props.DateDivorced,
      DivorceLocation: this.props.DivorceLocation,
      DivorceLocationNotApplicable: this.props.DivorceLocationNotApplicable,
      MarriageLocation: this.props.MarriageLocation,
      Status: this.props.Status,
      Deceased: this.props.Deceased,
      DeceasedAddress: this.props.DeceasedAddress,
      DeceasedDate: this.props.DeceasedDate,
      DeceasedAddressNotApplicable: this.props.DeceasedAddressNotApplicable,
      Comments: this.props.Comments,
      DivorcedAnnulledComments: this.props.DivorcedAnnulledComments,
      DivorcedAnnulledComments2: this.props.DivorcedAnnulledComments2,
      ...queue,
    });
  };

  updateName = (values: RelationshipsDivorcedItemModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updateBirthdate = (values: RelationshipsDivorcedItemModel['Birthdate']) => {
    this.update({
      Birthdate: values,
    });
  };

  updateBirthPlace = (values: RelationshipsDivorcedItemModel['BirthPlace']) => {
    this.update({
      BirthPlace: values,
    });
  };

  updateCitizenship = (values: RelationshipsDivorcedItemModel['Citizenship']) => {
    this.update({
      Citizenship: values,
    });
  };

  updateTelephone = (values: RelationshipsDivorcedItemModel['Telephone']) => {
    this.update({
      Telephone: values,
    });
  };

  updateRecognized = (values: RelationshipsDivorcedItemModel['Recognized']) => {
    this.update({
      Recognized: values,
    });
  };

  updateMarriageLocation = (values: RelationshipsDivorcedItemModel['MarriageLocation']) => {
    this.update({
      MarriageLocation: values,
    });
  };

  updateAddress = (values: RelationshipsDivorcedItemModel['Address']) => {
    this.update({
      Address: values,
    });
  };

  updateDateDivorced = (values: RelationshipsDivorcedItemModel['DateDivorced']) => {
    this.update({
      DateDivorced: values,
    });
  };

  updateDivorceLocation = (values: RelationshipsDivorcedItemModel['DivorceLocation']) => {
    this.update({
      DivorceLocation: values,
      DivorceLocationNotApplicable: { applicable: true },
    });
  };

  updateDivorceLocationNotApplicable = (divorceLocationNotApplicable: RelationshipsDivorcedItemModel['DivorceLocationNotApplicable']) => {
    const queue: Partial<RelationshipsDivorcedItemModel> = { DivorceLocationNotApplicable: divorceLocationNotApplicable };

    if (divorceLocationNotApplicable?.applicable === false) {
      const address: LocationModel | undefined = this.props.DivorceLocation;

      let country: CountryModel | string | CountryArrayModel | undefined = address?.country;

      if (country) {
        if (isSafeInternational(address)) {
          country = { value: 'INTERNATIONAL' } as CountryModel;
        }

        queue.DivorceLocation = { country } as LocationModel;
      } else {
        queue.DivorceLocation = {} as LocationModel;
      }
    }

    this.update(queue);
  };

  updateStatus = (values: RelationshipsDivorcedItemModel['Status']) => {
    const additional: Partial<RelationshipsDivorcedItemModel> = {};
    if (values.value !== 'Divorced' && values.value !== 'Annulled') {
      additional.DivorcedAnnulledComments = { name: 'DivorcedAnnulledComments', value: '' };
      additional.DivorcedAnnulledComments2 = { name: 'DivorcedAnnulledComments2', value: '' };
      additional.DivorceLocation = EmptyUSLocationInstance;
      additional.Deceased = { name: 'Deceased', value: '', checked: false };
      additional.DeceasedAddress = EmptyUSLocationInstance;
    }
    this.update({
      Status: values,
      ...additional
    });
  };

  updateDeceased = (values: RelationshipsDivorcedItemModel['Deceased']) => {
    const additional: Partial<RelationshipsDivorcedItemModel> = {};
    if (values.value !== 'No') {
      additional.DivorcedAnnulledComments2 = { name: 'DivorcedAnnulledComments2', value: '' };
      additional.DeceasedAddress = EmptyUSLocationInstance;
    }
    this.update({
      Deceased: values,
      ...additional
    });
  };

  updateDeceasedDate = (value: RelationshipsDivorcedItemModel['DeceasedDate']) => {
    this.update({
      DeceasedDate: value,
    });
  };

  updateDeceasedAddress = (values: RelationshipsDivorcedItemModel['DeceasedAddress']) => {
    this.update({
      DeceasedAddress: values,
    });
  };

  updateDeceasedAddressNotApplicable = (
    values: RelationshipsDivorcedItemModel['DeceasedAddressNotApplicable']
  ) => {
    const { DeceasedAddress } = this.props;
    this.update({
      DeceasedAddress: values.applicable ? DeceasedAddress : {},
      DeceasedAddressNotApplicable: values,
    });
  };

  updateComments = (values: RelationshipsDivorcedItemModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  updateDivorcedAnnulledComments = (values: RelationshipsDivorcedItemModel['DivorcedAnnulledComments']) => {
    this.update({
      DivorcedAnnulledComments: values,
    });
  };

  updateDivorcedAnnulledComments2 = (values: RelationshipsDivorcedItemModel['DivorcedAnnulledComments2']) => {
    this.update({
      DivorcedAnnulledComments2: values,
    });
  };

  render() {
    const { requireRelationshipMaritalDivorcePhoneNumber } = this.props;
    const enteredCivilUnionMinDate = pickDate([this.props.applicantBirthdate, this.props.Birthdate]);
    const attribute = `DivorcedList.items[${this.props.$index}].Item`;
    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div className="divorce">
        {(this.props.maritalStatus === 'Annulled'
          || this.props.maritalStatus === 'Divorced'
          || this.props.maritalStatus === 'Married'
          || this.props.maritalStatus === 'Spouse'
          || this.props.maritalStatus === 'Separated'
          || this.props.$index !== 0)
          && (
            <div>
              <Field
                title={i18n.t('relationships.pvq.civilUnion.divorce.heading.name')}
                optional
                filterErrors={Name.requiredErrorsOnly}
                scrollIntoView={this.props.scrollIntoView}
              >
                <PVQName
                  attribute={`${attribute}.Name`}
                  name="name"
                  className="divorce"
                  aria-label="Provide this person's full name"
                  {...this.props.Name}
                  onUpdate={this.updateName}
                  onError={this.handleNameError}
                  required={this.props.required}
                  scrollIntoView={this.props.scrollIntoView}
                />
              </Field>

              <Field
                attribute={`${attribute}.Birthdate`}
                help="relationships.civilUnion.divorce.help.birthdate"
                title={i18n.t('relationships.pvq.civilUnion.para.birthdate')}
                scrollIntoView={this.props.scrollIntoView}
                adjustFor="datecontrol"
              >
                <DateControl
                  attribute={`${attribute}.Birthdate`}
                  name="birthdate"
                  className="birthdate"
                  {...this.props.Birthdate}
                  relationship="Other"
                  onUpdate={this.updateBirthdate}
                  onError={this.props.onError}
                  required={this.props.required}
                />
              </Field>

              <Field
                attribute={`${attribute}.BirthPlace`}
                title={i18n.t('relationships.pvq.civilUnion.divorce.heading.birthplace')}
                scrollIntoView={this.props.scrollIntoView}
              >
                <LocationUnknown
                  name="birthplace"
                  attribute={`${attribute}.BirthPlace`}
                  {...this.props.BirthPlace}
                  layout={Location.IDENTIFICATION_BIRTH_PLACE}
                  className="birthplace"
                  label={i18n.t('relationships.pvq.civilUnion.label.birthplace')}
                  stateLabel={i18n.t('identification.pvq.birthplace.label.state')}
                  cityLabel={i18n.t('identification.pvq.birthplace.label.city')}
                  countyLabel={i18n.t('identification.pvq.birthplace.label.county')}
                  countryLabel={i18n.t('identification.pvq.birthplace.label.country')}
                  onUpdate={this.updateBirthPlace}
                  onError={this.props.onError}
                  required={this.props.required}
                />
              </Field>

              <Field
                attribute={`${attribute}.Citizenship`}
                title={i18n.t('relationships.pvq.civilUnion.divorce.heading.citizenship')}
                scrollIntoView={this.props.scrollIntoView}
              >
                <Country
                  attribute={`${attribute}.Citizenship`}
                  name="Citizenship"
                  multiple
                  {...this.props.Citizenship}
                  className="citizenship"
                  onError={this.props.onError}
                  onUpdate={this.updateCitizenship}
                  required={this.props.required}
                />
              </Field>
              <Field
                title={i18n.t('relationships.civilUnion.divorce.heading.deceased')}
                className="deceased"
                attribute={`${attribute}.Deceased`}
                exactAttribute
                scrollIntoView={this.props.scrollIntoView}
              >
                <RadioGroup
                  attribute={`${attribute}.Deceased`}
                  name="deceased"
                  className="option-list"
                  selectedValue={(this.props.Deceased || {}).value}
                  required={this.props.required}
                  onError={this.props.onError}
                >
                  <Radio
                    className="yes"
                    label={i18n.t('relationships.civilUnion.divorce.deceased.label.yes')}
                    value="Yes"
                    onUpdate={this.updateDeceased}
                    onError={this.props.onError}
                  />
                  <Radio
                    className="no widowed"
                    label={i18n.t('relationships.civilUnion.divorce.deceased.label.no')}
                    value="No"
                    onUpdate={this.updateDeceased}
                    onError={this.props.onError}
                  />
                </RadioGroup>
              </Field>
              {this.props.maritalStatus === 'Widowed'
                && this.props.$index === 0 && (
                  <Field
                    attribute={`${attribute}.DeceasedDate`}
                    title={i18n.t('relationships.pvq.civilUnion.divorce.heading.deceasedDate')}
                    scrollIntoView={this.props.scrollIntoView}
                    adjustFor="datecontrol"
                  >
                    <DateControl
                      attribute={`${attribute}.DeceasedDate`}
                      name="DeceasedDate"
                      prefix="deceasedDate"
                      className="date-deceased"
                      {...this.props.DeceasedDate}
                      onUpdate={this.updateDeceasedDate}
                      onError={this.props.onError}
                      required={this.props.required}
                    />
                  </Field>
              )}
              {this.props.Deceased?.value === 'No' && (
                <div>
                  <Field
                    attribute={`${attribute}.Telephone`}
                    title={i18n.t('relationships.pvq.civilUnion.divorce.heading.telephone')}
                    className="override-required"
                    scrollIntoView={this.props.scrollIntoView}
                    adjustFor="telephone"
                  >
                    <Telephone
                      attribute={`${attribute}.Telephone`}
                      name="Telephone"
                      {...this.props.Telephone}
                      onUpdate={this.updateTelephone}
                      onError={this.props.onError}
                      required={this.props.required}
                      showNumberType
                    />
                  </Field>
                  <Field
                    attribute={`${attribute}.Emails`}
                    title={i18n.t('relationships.pvq.civilUnion.divorce.heading.email')}
                    scrollIntoView={this.props.scrollIntoView}
                  >

                    <EmailCollection
                      attribute={`${attribute}.Emails`}
                      name="Emails"
                      {...this.props.Emails}
                      onError={this.props.onError}
                      required={this.props.required}
                      onUpdate={(values: RelationshipsDivorcedItemModel['Emails']) => this.update({
                        Emails: values,
                      })}
                      includeEmailType={false}
                      hasSecondaryAppendLabels
                      secondaryNoAppendLabel="No"
                      secondaryYesAppendLabel="Add"
                      showEmailUnknown
                    />
                  </Field>
                  <Field
                    attribute={`${attribute}.Recognized`}
                    title={i18n.t('relationships.pvq.civilUnion.heading.enteredCivilUnion')}
                    scrollIntoView={this.props.scrollIntoView}
                    adjustFor="datecontrol"
                  >
                    <DateControl
                      attribute={`${attribute}.Recognized`}
                      name="Recognized"
                      className="recognized"
                      minDateEqualTo
                      prefix="civilUnion"
                      minDate={enteredCivilUnionMinDate}
                      {...this.props.Recognized}
                      onUpdate={this.updateRecognized}
                      onError={this.props.onError}
                      required={this.props.required}
                    />
                  </Field>
                  <Field
                    title={i18n.t('relationships.pvq.civilUnion.heading.locationCivilUnion')}
                    scrollIntoView={this.props.scrollIntoView}
                    attribute={`${attribute}.MarriageLocation`}
                  >
                    <LocationUnknown
                      domesticTitle={i18n.t('relationships.pvq.civilUnion.heading.locationFollowupCivilUnion')}
                      foreignTitle={i18n.t('relationships.pvq.civilUnion.heading.locationFollowupCivilUnion')}
                      attribute={`${attribute}.MarriageLocation`}
                      name="marriageLocation"
                      {...this.props.MarriageLocation}
                      layout={Location.IDENTIFICATION_BIRTH_PLACE}
                      stateLabel={i18n.t('identification.pvq.birthplace.label.state')}
                      cityLabel={i18n.t('identification.pvq.birthplace.label.city')}
                      countyLabel={i18n.t('identification.pvq.birthplace.label.county')}
                      countryLabel={i18n.t('identification.pvq.birthplace.label.country')}
                      onUpdate={this.updateMarriageLocation}
                      onError={this.props.onError}
                      required={this.props.required}
                    />
                  </Field>
                  <Field
                    attribute={`${attribute}.DateDivorced`}
                    help="relationships.civilUnion.divorce.help.dateDivorced"
                    title={i18n.t('relationships.pvq.civilUnion.divorce.heading.dateDivorced')}
                    scrollIntoView={this.props.scrollIntoView}
                    adjustFor="datecontrol"
                  >
                    <DateControl
                      attribute={`${attribute}.DateDivorced`}
                      name="DateDivorced"
                      prefix="divorceDate"
                      className="date-divorced"
                      {...this.props.DateDivorced}
                      minDate={this.props.Recognized}
                      minDateEqualTo
                      onUpdate={this.updateDateDivorced}
                      onError={this.props.onError}
                      required={this.props.required}
                    />
                  </Field>
                  <Field
                    attribute={`${attribute}.DivorceLocation`}
                    onError={this.props.onError}
                    adjustFor="address"
                    shrink
                    scrollIntoView={this.props.scrollIntoView}
                  >
                    <LocationUnknown
                      attribute={`${attribute}.DivorceLocation`}
                      name="DivorceLocation"
                      {...this.props.DivorceLocation}
                      layout={Location.ADDRESS}
                      label={i18n.t('relationships.pvq.civilUnion.divorce.heading.divorceAddress')}
                      internationalStreetLabel={i18n.t('address.pvq.physicalAddress')}
                      stateLabel={i18n.t('identification.pvq.birthplace.label.state')}
                      cityLabel={i18n.t('identification.pvq.birthplace.label.city')}
                      countyLabel={i18n.t('identification.pvq.birthplace.label.county')}
                      countryLabel={i18n.t('identification.pvq.birthplace.label.country')}
                      onUpdate={this.updateDivorceLocation}
                      onError={this.handleError}
                      required={this.props.required}
                      showInternationalStreet
                      showMilitaryInstallation
                      showApoFpoDpoPostcodeWhenForeign
                      hideInstallationNameUnknownWhenForeign
                      showAddressUnknown
                      onUpdateAddressUnknown={this.updateDivorceLocationNotApplicable}
                      addressUnknown={this.props.DivorceLocationNotApplicable}
                    />
                  </Field>
                </div>
              )}
            </div>
          )}
        {this.props.maritalStatus === 'Widowed' && this.props.$index === 0 && (
          <div>
            <Field
              title={i18n.t('relationships.pvq.civilUnion.divorce.heading.nameWidow')}
              optional
              filterErrors={Name.requiredErrorsOnly}
              scrollIntoView={this.props.scrollIntoView}
            >
              <PVQName
                attribute={`${attribute}.Name`}
                name="name"
                className="divorce"
                aria-label="Provide this person's full name"
                {...this.props.Name}
                onUpdate={this.updateName}
                onError={this.handleNameError}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
              />
            </Field>

            <Field
              attribute={`${attribute}.dateWidow`}
              help="relationships.civilUnion.divorce.help.datewidow"
              title={i18n.t('relationships.pvq.civilUnion.divorce.heading.dateWidow')}
              scrollIntoView={this.props.scrollIntoView}
              adjustFor="datecontrol"
            >
              <DateControl
                attribute={`${attribute}.dateWidow`}
                name="dateWidow"
                className="dateWidow"
                {...this.props.DeceasedDate}
                relationship="Other"
                onUpdate={this.updateDeceasedDate}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
          </div>
        )}
      </div>
    ) : ( // SF86
      <div className="divorce">
        <Field
          attribute={`${attribute}.Name`}
          title={i18n.t('relationships.civilUnion.heading.name')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="labels"
        >
          <Name
            attribute={`${attribute}.Name`}
            name="Name"
            {...this.props.Name}
            aria-label="Provide this person's full name"
            onUpdate={this.updateName}
            onError={this.handleNameError}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.Birthdate`}
          help="relationships.civilUnion.divorce.help.birthdate"
          title={i18n.t('relationships.civilUnion.divorce.heading.birthdate')}
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="datecontrol"
        >
          <DateControl
            attribute={`${attribute}.Birthdate`}
            name="birthdate"
            className="birthdate"
            {...this.props.Birthdate}
            relationship="Other"
            onUpdate={this.updateBirthdate}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.BirthPlace`}
          title={i18n.t('relationships.civilUnion.divorce.heading.birthplace')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Location
            attribute={`${attribute}.BirthPlace`}
            name="birthplace"
            {...this.props.BirthPlace}
            layout={Location.BIRTHPLACE_WITHOUT_COUNTY}
            label={i18n.t('relationships.civilUnion.divorce.label.birthplace')}
            className="birthplace"
            onUpdate={this.updateBirthPlace}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.Citizenship`}
          title={i18n.t('relationships.civilUnion.divorce.heading.citizenship')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Country
            attribute={`${attribute}.Citizenship`}
            name="Citizenship"
            multiple
            {...this.props.Citizenship}
            className="citizenship"
            onError={this.props.onError}
            onUpdate={this.updateCitizenship}
            required={this.props.required}
          />
        </Field>

        {requireRelationshipMaritalDivorcePhoneNumber && (
          <Field
            attribute={`${attribute}.Telephone`}
            title={i18n.t('relationships.civilUnion.divorce.heading.telephone')}
            className="override-required"
            scrollIntoView={this.props.scrollIntoView}
            adjustFor="telephone"
          >
            <Telephone
              attribute={`${attribute}.Telephone`}
              name="Telephone"
              {...this.props.Telephone}
              onUpdate={this.updateTelephone}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}

        <Field
          attribute={`${attribute}.Recognized`}
          help="relationships.civilUnion.divorce.help.recognized"
          title={i18n.t('relationships.civilUnion.divorce.heading.recognized')}
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="datecontrol"
        >
          <DateControl
            attribute={`${attribute}.Recognized`}
            name="Recognized"
            minDateEqualTo
            prefix="civilUnion"
            minDate={enteredCivilUnionMinDate}
            className="recognized"
            {...this.props.Recognized}
            onUpdate={this.updateRecognized}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.Address`}
          title={i18n.t('relationships.civilUnion.divorce.heading.address')}
          optional
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="labels"
        >
          <Location
            attribute={`${attribute}.Address`}
            name="address"
            className="location"
            {...this.props.Address}
            layout={Location.BIRTHPLACE_WITHOUT_COUNTY}
            label={i18n.t('relationships.civilUnion.label.location')}
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.DateDivorced`}
          help="relationships.civilUnion.divorce.help.dateDivorced"
          title={i18n.t('relationships.civilUnion.divorce.heading.dateDivorced')}
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="datecontrol"
        >
          <DateControl
            attribute={`${attribute}.DateDivorced`}
            name="DateDivorced"
            prefix="divorceDate"
            className="date-divorced"
            {...this.props.DateDivorced}
            minDate={this.props.Recognized}
            minDateEqualTo
            onUpdate={this.updateDateDivorced}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.Status`}
          title={i18n.t('relationships.civilUnion.divorce.heading.status')}
          scrollIntoView={this.props.scrollIntoView}
          className="status"
        >
          <RadioGroup
            attribute="Status"
            name="status"
            className="option-list option-list-vertical"
            selectedValue={(this.props.Status || {}).value}
            required={this.props.required}
            onError={this.props.onError}
          >
            <Radio
              label={i18n.t('relationships.civilUnion.divorce.label.divorced')}
              value="Divorced"
              className="divorced"
              onUpdate={this.updateStatus}
              onError={this.props.onError}
            />
            <Radio
              label={i18n.t('relationships.civilUnion.divorce.label.widowed')}
              value="Widowed"
              className="widowed"
              onUpdate={this.updateStatus}
              onError={this.props.onError}
            />
            <Radio
              label={i18n.t('relationships.civilUnion.divorce.label.annulled')}
              value="Annulled"
              className="annulled"
              onUpdate={this.updateStatus}
              onError={this.props.onError}
            />
          </RadioGroup>
        </Field>
        <Comments
          title={i18n.t('relationships.comments.optional')}
          name={`${attribute}Comments`}
          className="optional-comment"
          attribute={`${attribute}Comments`}
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />

        <Show when={['Divorced', 'Annulled'].includes((this.props.Status ?? {}).value ?? '')}>
          <div>
            <Field
              attribute={`${attribute}.DivorceLocation`}
              title={i18n.t('relationships.civilUnion.divorce.heading.divorceLocation')}
              optional
              scrollIntoView={this.props.scrollIntoView}
              adjustFor="labels"
            >
              <Location
                attribute={`${attribute}.DivorceLocation`}
                className="divorce-location"
                {...this.props.DivorceLocation}
                layout={Location.US_CITY_STATE_ZIP_INTERNATIONAL_CITY}
                label={i18n.t('relationships.civilUnion.label.location')}
                onUpdate={this.updateDivorceLocation}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>

            <Field
              title={i18n.t('relationships.civilUnion.divorce.heading.deceased')}
              className="deceased"
              attribute={`${attribute}.Deceased`}
              exactAttribute
              scrollIntoView={this.props.scrollIntoView}
            >
              <RadioGroup
                attribute={`${attribute}.Deceased`}
                name="deceased"
                className="option-list"
                selectedValue={(this.props.Deceased || {}).value}
                required={this.props.required}
                onError={this.props.onError}
              >
                <Radio
                  className="yes"
                  label={i18n.t('relationships.civilUnion.divorce.deceased.label.yes')}
                  value="Yes"
                  onUpdate={this.updateDeceased}
                  onError={this.props.onError}
                />
                <Radio
                  className="no widowed"
                  label={i18n.t('relationships.civilUnion.divorce.deceased.label.no')}
                  value="No"
                  onUpdate={this.updateDeceased}
                  onError={this.props.onError}
                />
                <Radio
                  label={i18n.t('relationships.civilUnion.divorce.deceased.label.dontKnow')}
                  className="dk"
                  value="DK"
                  onUpdate={this.updateDeceased}
                  onError={this.props.onError}
                />
              </RadioGroup>
            </Field>
            <Comments
              title={i18n.t('relationships.comments.optional')}
              name={`${attribute}DivorcedAnnulledComments`}
              className="optional-comment"
              attribute={`${attribute}DivorcedAnnulledComments`}
              {...this.props.DivorcedAnnulledComments}
              onUpdate={this.updateDivorcedAnnulledComments}
              required={false}
            />

            <Show when={(this.props.Deceased || {}).value === 'No'}>
              <Field
                title={i18n.t('relationships.civilUnion.divorce.heading.deceasedAddress')}
                optional
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.DeceasedAddress`}
              >
                <NotApplicable
                  attribute={`${attribute}.DeceasedAddressNotApplicable`}
                  name="DeceasedAddressNotApplicable"
                  className="deceased-notapplicable"
                  {...this.props.DeceasedAddressNotApplicable}
                  label={i18n.t('relationships.civilUnion.deceasedAddressNotApplicable.label')}
                  or={i18n.m('relationships.civilUnion.deceasedAddressNotApplicable.or')}
                  onError={this.props.onError}
                  onUpdate={this.updateDeceasedAddressNotApplicable}
                >
                  <Location
                    attribute={`${attribute}.DeceasedAddress`}
                    className="address-deceased"
                    {...this.props.DeceasedAddress}
                    layout={Location.ADDRESS}
                    geocode
                    showPostOffice
                    onUpdate={this.updateDeceasedAddress}
                    onError={this.props.onError}
                    required={this.props.required}
                  />
                </NotApplicable>
              </Field>
              <Comments
                title={i18n.t('relationships.comments.optional')}
                name={`${attribute}DivorcedAnnulledComments2`}
                className="optional-comment"
                attribute={`${attribute}DivorcedAnnulledComments2`}
                {...this.props.DivorcedAnnulledComments2}
                onUpdate={this.updateDivorcedAnnulledComments2}
                required={false}
              />
            </Show>
          </div>
        </Show>
      </div>
    );
  }
}

Divorce.defaultProps = {
  Deceased: {},
  Emails: {}, // pvq only
  DeceasedAddressNotApplicable: { applicable: true },
  DivorceLocationNotApplicable: { applicable: true },
  Comments: {},
  DivorcedAnnulledComments: {},
  DivorcedAnnulledComments2: {},
  onUpdate: () => { },
  onError: (value, arr) => arr,
};
